import React, { useState, useEffect, useCallback } from 'react';
import { FaSignOutAlt, FaSave, FaEdit, FaTrash, FaPlus, FaUserTie, FaCode, FaProjectDiagram, FaInfoCircle } from 'react-icons/fa';
import { API_BASE } from '../config';

const Admin = () => {
  const [token, setToken] = useState(null);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loginError, setLoginError] = useState('');

  const [activeSection, setActiveSection] = useState('about');
  const [about, setAbout] = useState(null);
  const [projects, setProjects] = useState([]);
  const [skills, setSkills] = useState([]);
  const [experience, setExperience] = useState([]);

  const [editingItem, setEditingItem] = useState(null);
  const [editType, setEditType] = useState(null);
  const [formData, setFormData] = useState({});

  const fetchData = useCallback(async () => {
    const headers = {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    };

    try {
      const [aboutRes, projectsRes, skillsRes, expRes] = await Promise.all([
        fetch(`${API_BASE}/api/admin/about`, { headers }),
        fetch(`${API_BASE}/api/admin/projects`, { headers }),
        fetch(`${API_BASE}/api/admin/skills`, { headers }),
        fetch(`${API_BASE}/api/admin/experience`, { headers }),
      ]);

      if (aboutRes.ok) {
        const data = await aboutRes.json();
        setAbout(data);
        setFormData(data);
      }
      if (projectsRes.ok) {
        const data = await projectsRes.json();
        setProjects(data);
      }
      if (skillsRes.ok) {
        const data = await skillsRes.json();
        setSkills(data);
      }
      if (expRes.ok) {
        const data = await expRes.json();
        setExperience(data);
      }
    } catch (err) {
      console.error('Error fetching data:', err);
    }
  }, [token]);

  useEffect(() => {
    if (token) {
      fetchData();
    }
  }, [token, activeSection, fetchData]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginError('');

    try {
      const res = await fetch(`${API_BASE}/api/admin/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password }),
      });

      const data = await res.json();

      if (res.ok) {
        setToken(data.token);
      } else {
        setLoginError(data.error || 'Login failed');
      }
    } catch (err) {
      setLoginError('Network error');
    }
  };

  const handleLogout = () => {
    setToken(null);
  };

  const authHeaders = {
    'Authorization': `Bearer ${token}`,
    'Content-Type': 'application/json',
  };

  const resetForm = () => {
    setEditingItem(null);
    setEditType(null);
    setFormData({});
  };

  const startEdit = (item, type) => {
    setEditingItem(item);
    setEditType(type);
    setFormData({ ...item });
  };

  const startCreate = (type) => {
    let empty = {};
    if (type === 'projects') {
      empty = { title: '', description: '', image_url: '', technologies: [], github_link: '', live_link: '', category: '' };
    } else if (type === 'skills') {
      empty = { name: '', category: '', proficiency_level: 0 };
    } else if (type === 'experience') {
      empty = { company: '', position: '', start_date: '', end_date: '', description: '', location: '' };
    } else if (type === 'about') {
      empty = about || { name: '', title: '', bio: '', profile_image: '', email: '', phone: '', location: '', github: '', linkedin: '', twitter: '' };
    }
    setEditingItem(null);
    setEditType(type);
    setFormData(empty);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleArrayField = (e) => {
    const { name, value } = e.target;
    const arr = value.split(',').map(s => s.trim()).filter(s => s);
    setFormData(prev => ({ ...prev, [name]: arr }));
  };

  const submitForm = async (e) => {
    e.preventDefault();

    let url = `${API_BASE}/api/admin/${editType}`;
    let method = 'POST';

    if (editType === 'about') {
      url = `${API_BASE}/api/admin/about`;
      method = 'PUT';
    } else if (editingItem) {
      url = `${API_BASE}/api/admin/${editType}/${editingItem.id}`;
      method = 'PUT';
    }

    try {
      const res = await fetch(url, {
        method,
        headers: authHeaders,
        body: JSON.stringify(formData),
      });

      if (res.ok) {
        await fetchData();
        resetForm();
      } else {
        const data = await res.json();
        console.error('Save failed:', data.error);
      }
    } catch (err) {
      console.error('Save error:', err);
    }
  };

  const deleteItem = async (id, type) => {
    if (!window.confirm('Are you sure you want to delete this item?')) return;

    try {
      const res = await fetch(`${API_BASE}/api/admin/${type}/${id}`, {
        method: 'DELETE',
        headers: authHeaders,
      });

      if (res.ok) {
        await fetchData();
      }
    } catch (err) {
      console.error('Delete error:', err);
    }
  };

  if (!token) {
    return (
      <div className="admin-page">
        <div className="admin-login-container">
          <div className="admin-login-card">
            <h1 className="admin-login-title">Admin Login</h1>
            {loginError && <div className="admin-error-msg">{loginError}</div>}
            <form onSubmit={handleLogin} className="admin-login-form">
              <div className="admin-form-group">
                <label>Email</label>
                <input
                  type="email"
                  name="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="admin-input"
                  required
                />
              </div>
              <div className="admin-form-group">
                <label>Password</label>
                <input
                  type="password"
                  name="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="admin-input"
                  required
                />
              </div>
              <button type="submit" className="admin-btn admin-btn-primary">
                Login
              </button>
            </form>
          </div>
        </div>
      </div>
    );
  }

  const renderEditForm = () => {
    if (!editType) return null;

    const title = editingItem ? 'Edit' : 'Create';
    const singular = editType.slice(0, -1);

    let fields = null;
    if (editType === 'about') {
      fields = (
        <React.Fragment>
          <div className="admin-form-row">
            <div className="admin-form-group">
              <label>Name</label>
              <input type="text" name="name" value={formData.name || ''} onChange={handleInputChange} className="admin-input" />
            </div>
            <div className="admin-form-group">
              <label>Title</label>
              <input type="text" name="title" value={formData.title || ''} onChange={handleInputChange} className="admin-input" />
            </div>
          </div>
          <div className="admin-form-group">
            <label>Bio</label>
            <textarea name="bio" value={formData.bio || ''} onChange={handleInputChange} className="admin-input admin-textarea" rows="4" />
          </div>
          <div className="admin-form-row">
            <div className="admin-form-group">
              <label>Email</label>
              <input type="email" name="email" value={formData.email || ''} onChange={handleInputChange} className="admin-input" />
            </div>
            <div className="admin-form-group">
              <label>Phone</label>
              <input type="text" name="phone" value={formData.phone || ''} onChange={handleInputChange} className="admin-input" />
            </div>
          </div>
          <div className="admin-form-row">
            <div className="admin-form-group">
              <label>Location</label>
              <input type="text" name="location" value={formData.location || ''} onChange={handleInputChange} className="admin-input" />
            </div>
            <div className="admin-form-group">
              <label>Profile Image URL</label>
              <input type="text" name="profile_image" value={formData.profile_image || ''} onChange={handleInputChange} className="admin-input" />
            </div>
          </div>
          <div className="admin-form-row">
            <div className="admin-form-group">
              <label>GitHub</label>
              <input type="text" name="github" value={formData.github || ''} onChange={handleInputChange} className="admin-input" />
            </div>
            <div className="admin-form-group">
              <label>LinkedIn</label>
              <input type="text" name="linkedin" value={formData.linkedin || ''} onChange={handleInputChange} className="admin-input" />
            </div>
          </div>
          <div className="admin-form-group">
            <label>Twitter</label>
            <input type="text" name="twitter" value={formData.twitter || ''} onChange={handleInputChange} className="admin-input" />
          </div>
        </React.Fragment>
      );
    } else if (editType === 'projects') {
      fields = (
        <React.Fragment>
          <div className="admin-form-group">
            <label>Title</label>
            <input type="text" name="title" value={formData.title || ''} onChange={handleInputChange} className="admin-input" required />
          </div>
          <div className="admin-form-group">
            <label>Description</label>
            <textarea name="description" value={formData.description || ''} onChange={handleInputChange} className="admin-input admin-textarea" rows="3" />
          </div>
          <div className="admin-form-group">
            <label>Image URL</label>
            <input type="text" name="image_url" value={formData.image_url || ''} onChange={handleInputChange} className="admin-input" />
          </div>
          <div className="admin-form-group">
            <label>Technologies (comma separated)</label>
            <input type="text" name="technologies" value={(formData.technologies || []).join(', ')} onChange={handleArrayField} className="admin-input" />
          </div>
          <div className="admin-form-row">
            <div className="admin-form-group">
              <label>GitHub Link</label>
              <input type="text" name="github_link" value={formData.github_link || ''} onChange={handleInputChange} className="admin-input" />
            </div>
            <div className="admin-form-group">
              <label>Live Link</label>
              <input type="text" name="live_link" value={formData.live_link || ''} onChange={handleInputChange} className="admin-input" />
            </div>
          </div>
          <div className="admin-form-group">
            <label>Category</label>
            <input type="text" name="category" value={formData.category || ''} onChange={handleInputChange} className="admin-input" placeholder="e.g. Full Stack, AI/ML, Hardware" />
          </div>
        </React.Fragment>
      );
    } else if (editType === 'skills') {
      fields = (
        <React.Fragment>
          <div className="admin-form-row">
            <div className="admin-form-group">
              <label>Name</label>
              <input type="text" name="name" value={formData.name || ''} onChange={handleInputChange} className="admin-input" required />
            </div>
            <div className="admin-form-group">
              <label>Category</label>
              <input type="text" name="category" value={formData.category || ''} onChange={handleInputChange} className="admin-input" required />
            </div>
          </div>
          <div className="admin-form-group">
            <label>Proficiency Level (0-100)</label>
            <input type="number" name="proficiency_level" value={formData.proficiency_level || 0} onChange={handleInputChange} className="admin-input" min="0" max="100" />
          </div>
        </React.Fragment>
      );
    } else if (editType === 'experience') {
      fields = (
        <React.Fragment>
          <div className="admin-form-row">
            <div className="admin-form-group">
              <label>Company</label>
              <input type="text" name="company" value={formData.company || ''} onChange={handleInputChange} className="admin-input" required />
            </div>
            <div className="admin-form-group">
              <label>Position</label>
              <input type="text" name="position" value={formData.position || ''} onChange={handleInputChange} className="admin-input" required />
            </div>
          </div>
          <div className="admin-form-row">
            <div className="admin-form-group">
              <label>Start Date</label>
              <input type="date" name="start_date" value={formData.start_date || ''} onChange={handleInputChange} className="admin-input" />
            </div>
            <div className="admin-form-group">
              <label>End Date</label>
              <input type="date" name="end_date" value={formData.end_date || ''} onChange={handleInputChange} className="admin-input" />
            </div>
          </div>
          <div className="admin-form-group">
            <label>Description</label>
            <textarea name="description" value={formData.description || ''} onChange={handleInputChange} className="admin-input admin-textarea" rows="3" />
          </div>
          <div className="admin-form-group">
            <label>Location</label>
            <input type="text" name="location" value={formData.location || ''} onChange={handleInputChange} className="admin-input" />
          </div>
        </React.Fragment>
      );
    }

    return (
      <div className="admin-edit-panel">
        <h2>{title} {singular}</h2>
        <form onSubmit={submitForm} className="admin-edit-form">
          {fields}
          <div className="admin-form-actions">
            <button type="submit" className="admin-btn admin-btn-primary">
              <FaSave /> Save
            </button>
            <button type="button" className="admin-btn admin-btn-secondary" onClick={resetForm}>
              Cancel
            </button>
          </div>
        </form>
      </div>
    );
  };

  const renderListSection = () => {
    if (activeSection === 'about') {
      return (
        <div>
          <div className="admin-section-header">
            <h2>About Section</h2>
            <button className="admin-btn admin-btn-primary" onClick={() => startCreate('about')}>
              <FaEdit /> Edit
            </button>
          </div>
          {about && (
            <div className="admin-about-view">
              <p><strong>Name:</strong> {about.name}</p>
              <p><strong>Title:</strong> {about.title}</p>
              <p><strong>Email:</strong> {about.email}</p>
              <p><strong>Location:</strong> {about.location}</p>
              <p><strong>Bio:</strong> {about.bio}</p>
            </div>
          )}
        </div>
      );
    }

    if (activeSection === 'projects') {
      return (
        <div>
          <div className="admin-section-header">
            <h2>Projects</h2>
            <button className="admin-btn admin-btn-primary" onClick={() => startCreate('projects')}>
              <FaPlus /> Add Project
            </button>
          </div>
          <table className="admin-table">
            <thead>
              <tr>
                <th>ID</th><th>Title</th><th>Category</th><th>GitHub</th><th>Live</th><th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {projects.map(p => (
                <tr key={p.id}>
                  <td>{p.id}</td>
                  <td>{p.title}</td>
                  <td>{p.category || '-'}</td>
                  <td>{p.github_link ? 'Yes' : '-'}</td>
                  <td>{p.live_link ? 'Yes' : '-'}</td>
                  <td>
                    <button className="admin-table-btn admin-edit-btn" onClick={() => startEdit(p, 'projects')}><FaEdit /></button>
                    <button className="admin-table-btn admin-delete-btn" onClick={() => deleteItem(p.id, 'projects')}><FaTrash /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    }

    if (activeSection === 'skills') {
      return (
        <div>
          <div className="admin-section-header">
            <h2>Skills</h2>
            <button className="admin-btn admin-btn-primary" onClick={() => startCreate('skills')}>
              <FaPlus /> Add Skill
            </button>
          </div>
          <table className="admin-table">
            <thead>
              <tr>
                <th>ID</th><th>Name</th><th>Category</th><th>Proficiency</th><th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {skills.map(s => (
                <tr key={s.id}>
                  <td>{s.id}</td>
                  <td>{s.name}</td>
                  <td>{s.category}</td>
                  <td>{s.proficiency_level}%</td>
                  <td>
                    <button className="admin-table-btn admin-edit-btn" onClick={() => startEdit(s, 'skills')}><FaEdit /></button>
                    <button className="admin-table-btn admin-delete-btn" onClick={() => deleteItem(s.id, 'skills')}><FaTrash /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    }

    if (activeSection === 'experience') {
      return (
        <div>
          <div className="admin-section-header">
            <h2>Experience</h2>
            <button className="admin-btn admin-btn-primary" onClick={() => startCreate('experience')}>
              <FaPlus /> Add Experience
            </button>
          </div>
          <table className="admin-table">
            <thead>
              <tr>
                <th>ID</th><th>Company</th><th>Position</th><th>Start Date</th><th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {experience.map(e => (
                <tr key={e.id}>
                  <td>{e.id}</td>
                  <td>{e.company}</td>
                  <td>{e.position}</td>
                  <td>{e.start_date}</td>
                  <td>
                    <button className="admin-table-btn admin-edit-btn" onClick={() => startEdit(e, 'experience')}><FaEdit /></button>
                    <button className="admin-table-btn admin-delete-btn" onClick={() => deleteItem(e.id, 'experience')}><FaTrash /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    }

    return null;
  };

  return (
    <div className="admin-page">
      <div className="admin-header">
        <div className="admin-header-content">
          <h1 className="admin-title">Admin Dashboard</h1>
          <button className="admin-btn admin-btn-logout" onClick={handleLogout}>
            <FaSignOutAlt /> Logout
          </button>
        </div>
        <div className="admin-nav">
          <button
            className={`admin-nav-btn ${activeSection === 'about' ? 'active' : ''}`}
            onClick={() => { setActiveSection('about'); resetForm(); }}
          >
            <FaInfoCircle /> About
          </button>
          <button
            className={`admin-nav-btn ${activeSection === 'projects' ? 'active' : ''}`}
            onClick={() => { setActiveSection('projects'); resetForm(); }}
          >
            <FaProjectDiagram /> Projects
          </button>
          <button
            className={`admin-nav-btn ${activeSection === 'skills' ? 'active' : ''}`}
            onClick={() => { setActiveSection('skills'); resetForm(); }}
          >
            <FaCode /> Skills
          </button>
          <button
            className={`admin-nav-btn ${activeSection === 'experience' ? 'active' : ''}`}
            onClick={() => { setActiveSection('experience'); resetForm(); }}
          >
            <FaUserTie /> Experience
          </button>
        </div>
      </div>

      <div className="admin-content">
        {renderEditForm()}
        {renderListSection()}
      </div>
    </div>
  );
};

export default Admin;
